import React, { useEffect, useMemo, useState } from 'react'
import { adminApi } from '@/services/adminApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Save, Mail } from 'lucide-react'
import { toast } from 'sonner'
import type {
  EmailAssistantGlobalConfig,
  PipelineStep,
  PipelineModelEntry,
} from '@/types/admin'

interface LLMModel {
  id: string
  name: string
  displayName: string | null
  providerId: string
  provider: { id: string; name: string }
}

const PIPELINE_STEPS: { key: PipelineStep; label: string; tier: 'triage' | 'agent' | 'standalone' }[] = [
  { key: 'setupGenerate',   label: 'Setup — Suggest labels',                   tier: 'triage' },
  { key: 'setupRefine',     label: 'Setup — Pick final 20 labels',             tier: 'agent' },
  { key: 'setupCategorize', label: 'Setup — Assign labels to existing emails', tier: 'triage' },
  { key: 'liveTriage',      label: 'Live triage (every incoming email)',       tier: 'triage' },
  { key: 'draftAgent',      label: 'Draft agent (per-reply agent loop)',       tier: 'agent' },
  { key: 'writingStyle',    label: 'Writing-style analysis',                   tier: 'standalone' },
  { key: 'toneSample',      label: 'Tone-sample preview',                      tier: 'standalone' },
]
const DEFAULT_PIPELINE_ENTRY: PipelineModelEntry = { provider: 'openai', model: 'gpt-5.4-nano' }

export default function EmailAssistantSettingsPage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedConfig, setSavedConfig] = useState<EmailAssistantGlobalConfig | null>(null)
  const [llmModels, setLlmModels] = useState<LLMModel[]>([])
  const [config, setConfig] = useState<EmailAssistantGlobalConfig>({
    freePlanDraftLimit: 100,
    proPlanDraftLimit: 300,
    emailAgeThresholdMinutes: 30,
    labelConfidenceThreshold: 0.6,
    setupSampleSize: 800,
    setupCategorizeWindowDays: 30,
    setupGenerateBatchSize: 300,
    setupCategorizeBatchSize: 150,
    triageThreadContextDepth: 3,
    draftBodyMaxChars: 4000,
    pipelineModels: Object.fromEntries(
      PIPELINE_STEPS.map(s => [s.key, { ...DEFAULT_PIPELINE_ENTRY }])
    ) as EmailAssistantGlobalConfig['pipelineModels'],
  })

  useEffect(() => {
    Promise.all([
      adminApi.getEmailAssistantConfig(),
      adminApi.getAllLLMModels().catch(() => [] as LLMModel[]),
    ])
      .then(([loaded, models]) => {
        setLlmModels(models as LLMModel[])
        // Merge with defaults so if pipelineModels is missing on the
        // server, we fall back to local defaults rather than crashing
        // on indexed access.
        setConfig(prev => {
          const merged = {
            ...prev,
            ...loaded,
            pipelineModels: {
              ...prev.pipelineModels,
              ...(loaded.pipelineModels ?? {}),
            },
          }
          setSavedConfig(merged)
          return merged
        })
      })
      .catch((e: any) => toast.error(e?.response?.data?.error || e?.message || 'Failed to load config'))
      .finally(() => setLoading(false))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      // Send only the steps that changed: the server partial-merges, and an
      // untouched step whose stored model has since left the catalog must not
      // block saving the rest.
      const changedSteps = Object.fromEntries(
        PIPELINE_STEPS.flatMap(({ key }) => {
          const next = config.pipelineModels?.[key]
          const prev = savedConfig?.pipelineModels?.[key]
          if (!next) return []
          if (prev && prev.provider === next.provider && prev.model === next.model) return []
          return [[key, next]]
        }),
      )
      const { pipelineModels: _all, ...scalars } = config
      const payload =
        Object.keys(changedSteps).length > 0
          ? { ...scalars, pipelineModels: changedSteps as EmailAssistantGlobalConfig['pipelineModels'] }
          : scalars
      const updated = await adminApi.updateEmailAssistantConfig(payload)
      const merged = {
        ...updated,
        pipelineModels: { ...config.pipelineModels, ...(updated.pipelineModels ?? {}) },
      }
      setConfig(merged)
      setSavedConfig(merged)
      toast.success('Email assistant config saved')
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || 'Failed to save config')
    } finally {
      setSaving(false)
    }
  }

  const handleChange = (field: keyof EmailAssistantGlobalConfig, value: string) => {
    const num = field === 'labelConfidenceThreshold' ? parseFloat(value) : parseInt(value, 10)
    if (!isNaN(num)) {
      setConfig(prev => ({ ...prev, [field]: num }))
    }
  }

  const handlePipelineModelChange = (
    step: PipelineStep,
    field: 'provider' | 'model',
    value: string,
  ) => {
    setConfig(prev => {
      const current = prev.pipelineModels?.[step] ?? { ...DEFAULT_PIPELINE_ENTRY }
      // Switching provider clears the model so a stale model from the
      // previous provider can't linger.
      const next =
        field === 'provider'
          ? { provider: value, model: '' }
          : { ...current, model: value }
      return {
        ...prev,
        pipelineModels: {
          ...(prev.pipelineModels ?? {}),
          [step]: next,
        },
      }
    })
  }

  // Provider + per-provider model lists, derived from the LLM model catalog —
  // the same source the Agent Config page uses.
  const providers = useMemo(
    () =>
      Array.from(new Map(llmModels.map(m => [m.provider.name, m.provider])).values()).sort(
        (a, b) => a.name.localeCompare(b.name),
      ),
    [llmModels],
  )
  const modelsByProvider = useMemo(() => {
    const map = new Map<string, LLMModel[]>()
    for (const m of llmModels) {
      const list = map.get(m.provider.name) ?? []
      list.push(m)
      map.set(m.provider.name, list)
    }
    for (const list of map.values()) {
      list.sort((a, b) => (a.displayName ?? a.name).localeCompare(b.displayName ?? b.name))
    }
    return map
  }, [llmModels])

  const dirty = savedConfig !== null && JSON.stringify(config) !== JSON.stringify(savedConfig)
  // A provider switch clears the model; the server rejects an empty model, so
  // block Save until every touched step is complete.
  const incompleteSteps = PIPELINE_STEPS.filter(s => {
    const entry = config.pipelineModels?.[s.key]
    return Boolean(entry?.provider) && !entry?.model
  })
  const canSave = dirty && !saving && incompleteSteps.length === 0

  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const saveControls = (
    <div className="flex items-center gap-3">
      {incompleteSteps.length > 0 ? (
        <span className="text-sm text-red-600">
          Pick a model for: {incompleteSteps.map(s => s.label).join(', ')}
        </span>
      ) : dirty ? (
        <span className="text-sm text-amber-600">Unsaved changes</span>
      ) : (
        <span className="text-sm text-gray-400">All changes saved</span>
      )}
      <Button onClick={handleSave} disabled={!canSave} className="flex items-center gap-2">
        <Save className="h-4 w-4" />
        {saving ? 'Saving…' : 'Save Changes'}
      </Button>
    </div>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div>
      <div className="sticky top-0 z-10 -mx-6 px-6 py-3 mb-6 bg-gray-50/95 backdrop-blur border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mail className="h-6 w-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Email Assistant Settings</h1>
            <p className="text-sm text-gray-500 mt-0.5">Global configuration for email assistant behaviour and limits</p>
          </div>
        </div>
        {saveControls}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Draft Limits */}
        <div className="border border-gray-200 rounded-lg p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Draft Limits (per month)</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Free Plan
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  value={config.freePlanDraftLimit}
                  onChange={e => handleChange('freePlanDraftLimit', e.target.value)}
                  className="w-32"
                />
                <span className="text-sm text-gray-500">drafts / month</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pro Plan
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  value={config.proPlanDraftLimit}
                  onChange={e => handleChange('proPlanDraftLimit', e.target.value)}
                  className="w-32"
                />
                <span className="text-sm text-gray-500">drafts / month</span>
              </div>
            </div>
          </div>
        </div>

        {/* Auto-Draft Rules */}
        <div className="border border-gray-200 rounded-lg p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Auto-Draft Rules</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Age Threshold
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  value={config.emailAgeThresholdMinutes}
                  onChange={e => handleChange('emailAgeThresholdMinutes', e.target.value)}
                  className="w-32"
                />
                <span className="text-sm text-gray-500">minutes</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Emails older than this will not receive an auto-draft reply
              </p>
            </div>
          </div>
        </div>

        {/* Auto-Label Rules */}
        <div className="border border-gray-200 rounded-lg p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Auto-Label Rules</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Label Confidence Threshold
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={config.labelConfidenceThreshold}
                  onChange={e => handleChange('labelConfidenceThreshold', e.target.value)}
                  className="w-32"
                />
                <span className="text-sm text-gray-500">
                  ({Math.round(config.labelConfidenceThreshold * 100)}%)
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Minimum confidence score (0–1) required to apply a label automatically
              </p>
            </div>
          </div>
        </div>

        {/* Onboarding (one-time setup) */}
        <div className="border border-gray-200 rounded-lg p-5 md:col-span-2">
          <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">
            Onboarding (First-Time Auto-Label Setup)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Emails to Read on Setup
              </label>
              <Input
                type="number"
                min={100}
                max={5000}
                value={config.setupSampleSize}
                onChange={e => handleChange('setupSampleSize', e.target.value)}
                className="w-32"
              />
              <p className="text-xs text-gray-400 mt-1">
                Recent emails the AI samples to learn label names.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Label Old Emails Up To (days)
              </label>
              <Input
                type="number"
                min={0}
                max={365}
                value={config.setupCategorizeWindowDays}
                onChange={e => handleChange('setupCategorizeWindowDays', e.target.value)}
                className="w-32"
              />
              <p className="text-xs text-gray-400 mt-1">
                Older mail is skipped. 0 = label all.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Emails per AI Call (Suggesting Labels)
              </label>
              <Input
                type="number"
                min={25}
                max={1000}
                value={config.setupGenerateBatchSize}
                onChange={e => handleChange('setupGenerateBatchSize', e.target.value)}
                className="w-32"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Emails per AI Call (Assigning Labels)
              </label>
              <Input
                type="number"
                min={25}
                max={500}
                value={config.setupCategorizeBatchSize}
                onChange={e => handleChange('setupCategorizeBatchSize', e.target.value)}
                className="w-32"
              />
            </div>
          </div>
        </div>

        {/* Per-Step Models */}
        <div className="border border-gray-200 rounded-lg p-5 md:col-span-2">
          <h2 className="text-sm font-semibold text-gray-700 mb-1 uppercase tracking-wide">
            Per-Step Models
          </h2>
          <p className="text-xs text-gray-400 mb-4">
            Org-level <code className="text-[11px]">triageModel</code>/<code className="text-[11px]">agentModel</code> still wins for tier-tagged steps when set.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase text-gray-500">
                  <th className="text-left font-medium py-2 pr-4">Step</th>
                  <th className="text-left font-medium py-2 pr-4">Tier</th>
                  <th className="text-left font-medium py-2 pr-4">Provider</th>
                  <th className="text-left font-medium py-2">Model</th>
                </tr>
              </thead>
              <tbody>
                {PIPELINE_STEPS.map(({ key, label, tier }) => {
                  const entry = config.pipelineModels?.[key] ?? DEFAULT_PIPELINE_ENTRY
                  const models = modelsByProvider.get(entry.provider) ?? []
                  const providerKnown = !entry.provider || providers.some(p => p.name === entry.provider)
                  const modelKnown = !entry.model || models.some(m => m.name === entry.model)
                  return (
                    <tr key={key} className="border-b border-gray-100 last:border-0">
                      <td className="py-2 pr-4">{label}</td>
                      <td className="py-2 pr-4 text-xs text-gray-500">{tier}</td>
                      <td className="py-2 pr-4">
                        <select
                          value={entry.provider}
                          onChange={e => handlePipelineModelChange(key, 'provider', e.target.value)}
                          className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          {!entry.provider && <option value="">Provider</option>}
                          {providers.map(p => (
                            <option key={p.id} value={p.name}>{p.name}</option>
                          ))}
                          {!providerKnown && <option value={entry.provider}>{entry.provider}</option>}
                        </select>
                      </td>
                      <td className="py-2">
                        <select
                          value={entry.model}
                          onChange={e => handlePipelineModelChange(key, 'model', e.target.value)}
                          disabled={!entry.provider}
                          className="h-8 w-56 rounded-md border border-gray-300 bg-white px-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                        >
                          <option value="">{entry.provider ? 'Model' : '—'}</option>
                          {models.map(m => (
                            <option key={m.id} value={m.name}>{m.displayName ?? m.name}</option>
                          ))}
                          {!modelKnown && <option value={entry.model}>{entry.model}</option>}
                        </select>
                        {entry.model && !modelKnown && (
                          <p className="mt-1 text-xs text-amber-600">Not in the model catalog — pick a listed model.</p>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Per-Email AI Behavior */}
        <div className="border border-gray-200 rounded-lg p-5 md:col-span-2">
          <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">
            Per-Email AI Behavior
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Thread History Sent to AI (messages)
              </label>
              <Input
                type="number"
                min={0}
                max={20}
                value={config.triageThreadContextDepth}
                onChange={e => handleChange('triageThreadContextDepth', e.target.value)}
                className="w-32"
              />
              <p className="text-xs text-gray-400 mt-1">
                Earlier thread messages shown to the AI. 0 = each email judged in isolation.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Body Limit for Draft AI (characters)
              </label>
              <Input
                type="number"
                min={500}
                max={16000}
                step={500}
                value={config.draftBodyMaxChars}
                onChange={e => handleChange('draftBodyMaxChars', e.target.value)}
                className="w-32"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">{saveControls}</div>
    </div>
  )
}
